import {
  JudgementADDDocumentType,
  JudgementOnMeritsVictim,
} from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/typings';
import {
  PROCEDURE_STATUSES,
  PROCEDURE_STEPS,
  REJECTION_REASON,
  VICTIM_EXPERTISE_FIELDS,
} from 'containers/data-structure';
import {
  EXPERTISE_ACCOUNTING_ID,
  EXPERTISE_MEDICAL_ID,
} from 'containers/CourtManagerLegalProceedings/constants';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import { difference, get, isEmpty, isNil, uniq } from 'lodash';
import {
  getCivilJudgementByVictimId,
  getRateItt,
  reasonWithoutCompensationConditions,
} from 'containers/CourtManagerLegalProceedings/JudgementOnMerits/utils';
import {
  getResponsibilityShareByType,
  getSubstantiveWithoutCompensation,
  isCivilJudgementVictimSubstantive,
  isNextOfKinsSubstantive,
  isNextOfKinsWithFormRejection,
} from 'containers/CourtManagerLegalProceedings/AppealOnMerits/utils';
import { resolveDeceasedVictimFees } from 'containers/CourtManagerLegalProceedings/JudgementOnMerits/utils';
import {
  filterCivilJudgement,
  getAllJudgementAddDocuments,
  getInjuredCivilJudgement,
  resolveJudiciaryProcessType,
} from 'containers/LawyerLegalProceedingsPage/JudgementOnMerits/utils';
import messages from 'containers/LawyerLegalProceedingsPage/AppealOnMerits/messages';
import currentIntervenantStorage from 'containers/services/security/current-intervenant-storage';
import { JudgementOnMeritsType } from 'containers/LawyerLegalProceedingsPage/typings';
import { formatCourtFileNumber } from 'containers/LawyerLegalProceedingsPage/judgementAdd/utils';
import { translate } from 'containers/services/translate';
import {
  checkVictimExistanceCivilPartyById,
  isJomAssociatedVictimsIncluded,
} from 'containers/CourtManagerLegalProceedings/JudgementOnMerits/utils';
import { INJURY_POST_DECEASED } from 'containers/PreOpeningPage/constants';
import {
  getVictimAge,
  getVictimKey,
  getVictimRevenu,
  resolveExpertiseValue,
} from 'containers/CourtManagerLegalProceedings/ExpertReport/utils';
import { CURRENT_JUDICIARY_STEP_JUDGEMENT_ON_MERITS } from 'containers/CourtManagerLegalProceedings/JudgementOnMerits/constants';
import { MODE } from 'containers/CourtManagerLegalProceedings/ExpertReport/constants';
import {
  checkVictimExistanceCivilParty,
  getVictimIds,
} from 'containers/LawyerLegalProceedingsPage/JudgementOnMerits/utils';
import { VictimType } from 'typings/globalTypes';
import { GLOBAL_STATUS } from 'services/constants';
import { MEDICAL_EXPERTISE_ID } from 'containers/LawyerLegalProceedingsPage/JudgementOnMerits/constants';
import { getTheLatestEvaluationObject } from 'containers/LawyerLegalProceedingsPage/judgementAdd/IncomeCertificate/utils';
import moment from 'moment';

export const createCivilJudgementPayload = (
  withFeeNote: boolean,
  claimId: number,
  claimNumber: string,
  judgementOnMeritsId: number,
  judiciaryProcessId: number,
  civilJudgements: any,
  feeNoteAmounts: any,
  lawyerInfo: any,
) => {
  return {
    judgementOnMeritsId,
    judiciaryProcessId,
    claimId,
    claimNumber,
    civjs: civilJudgements,
    feeNote: withFeeNote
      ? {
          step: PROCEDURE_STEPS.ARRET_ON_MERITS,
          codeIntervenant: lawyerInfo.code,
        }
      : undefined,
  };
};
export const getJudgementAddDocuments = (
  appealAddDocuments: JudgementADDDocumentType[],
  judgementAddFirstInstance: any,
  judgementOnMerits: JudgementOnMeritsType,
) => {
  const injuredJudgementAddDocuments = getInjuredCivilJudgement(
    appealAddDocuments.map(item => ({ ...item, isAppeal: true })),
  );

  const injuredVictimsDocuments = getInjuredCivilJudgement(
    judgementAddFirstInstance,
  );

  const filteredJudgementAddDocuments = filterCivilJudgement(
    judgementOnMerits,
    injuredJudgementAddDocuments,
  );
  const allJudgementDocuments = getAllJudgementAddDocuments(
    injuredVictimsDocuments,
    filteredJudgementAddDocuments,
  );

  return allJudgementDocuments;
};

export const resolveJudgementAddAppeal = (
  allJudgementAddDocuments: any,
  isMultiple: boolean | undefined,
  currentJom: any,
) => {
  if (!isMultiple) return allJudgementAddDocuments;
  const victimsIds = get(currentJom, 'judgementOnMerits.jom.civjs', [])
    .map((el: any) =>
      [
        ...get(el, 'civjacce', []).map((exp: any) => get(exp, 'victim.id')),
        ...get(el, 'civjmede', []).map((exp: any) => get(exp, 'victim.id')),
      ].flat(),
    )
    .flat();
  const vehiclesIds = get(currentJom, 'judgementOnMerits.jom.civjs', [])
    .map((el: any) =>
      [
        ...get(el, 'civjmece', []).map((exp: any) => get(exp, 'vehicule.id')),
        ...get(el, 'civjacce', []).map((exp: any) => get(exp, 'vehicule.id')),
      ].flat(),
    )
    .flat();
  return allJudgementAddDocuments
    .map((el: any) => {
      return {
        ...el,
        expertises: el.expertises
          .map((exp: any) => {
            return {
              ...exp,
              victims: get(exp, 'victims', []).filter((el: any) =>
                victimsIds.includes(el.id),
              ),
              vehicules: get(exp, 'vehicules', []).filter((el: any) =>
                vehiclesIds.includes(el.id),
              ),
            };
          })
          .filter(
            (el: any) =>
              !isEmpty(get(el, 'victims', [])) ||
              !isEmpty(get(el, 'vehicules', [])),
          ),
      };
    })
    .filter((el: any) => !isEmpty(get(el, 'expertises', [])));
};

export const formatFeeNote = (
  lawyerInfo: any,
  judgementOnMerits: any,
  filtredCvilProceeding: any,
  lawyerFirmReference: any,
  claimNumber: string,
  judgementOnMeritsInfo: any,
) => {
  return {
    step: PROCEDURE_STEPS.ARRET_ON_MERITS,
    codeIntervenant: lawyerInfo.code,
    judgementDate: get(
      judgementOnMerits,
      'judgementOnMerits.jom.judgementDate',
      null,
    ),
    judgementId: get(judgementOnMerits, 'judgementOnMerits.id', null),
    firmReference: lawyerFirmReference,
    status:
      get(judgementOnMerits, 'judgementOnMerits.status', null) ===
      PROCEDURE_STATUSES.REJECTED
        ? PROCEDURE_STATUSES.REJECTED
        : PROCEDURE_STATUSES.PENDING,
    comment:
      get(judgementOnMerits, 'judgementOnMerits.status', null) ===
      PROCEDURE_STATUSES.REJECTED
        ? REJECTION_REASON
        : null,
    claimNumber,
    courtNumber: `${resolveJudiciaryProcessType(
      get(filtredCvilProceeding, 'procedureNatureId'),
    )} ${formatCourtFileNumber(get(judgementOnMeritsInfo, 'judgementNumber'))}`,
  };
};

export const renderButtonValidationTitle = (
  lawyerFirmReference: any,
  judgementOnMerits: any,
  feeNoteAssignment: any,
  props: any,
) => {
  return lawyerFirmReference === null && isEmpty(judgementOnMerits)
    ? translate(messages.saveButton, props)
    : !isEmpty(judgementOnMerits) && !feeNoteAssignment
    ? translate(messages.generateFeeNote, props)
    : translate(messages.editButton, props);
};

export const renderMessageFormReference = (
  lawyerFirmReference: any,
  judgementOnMerits: any,
  feeNoteAssignment: any,
  props: any,
) => {
  return lawyerFirmReference === null &&
    (feeNoteAssignment || isEmpty(judgementOnMerits))
    ? translate(messages.lawyerFirmReferenceUpdateMessage, props)
    : translate(messages.lawyerFirmReferenceEditMessage, props);
};

export const isEligibleVictim = (
  victim: VictimType,
  beneficiaries: any,
  sagisEvalVictimKeys: any,
  civilDocumentsVictimKeys: any,
  isFirst: boolean,
  victimsEvaluationsToInclude: any,
  isMultiple: boolean,
  civilJudgementVictimReasonListings: any,
) => {
  const victimBeneficiaries = beneficiaries.filter(
    (b: any) => b?.victimId === victim?.id,
  );

  return (
    victim[VICTIM_EXPERTISE_FIELDS.INJURY_POST_KEY] === INJURY_POST_DECEASED &&
    !sagisEvalVictimKeys.includes(victim.victimKey) &&
    !civilDocumentsVictimKeys.includes(victim.victimKey) &&
    isJomAssociatedVictimsIncluded(
      isMultiple || isFirst,
      (victim.id as unknown) as number,
      victimsEvaluationsToInclude,
    ) &&
    !isNextOfKinsWithFormRejection(
      victimBeneficiaries,
      civilJudgementVictimReasonListings,
    )
  );
};

export const getVictimRevenuSafe = (
  victimId: number,
  fallback: any,
  victims: any,
  judiciaryProcessId: any,
) => {
  const revenu = getVictimRevenu(victimId, victims, judiciaryProcessId);

  return !isNil(revenu) ? revenu : fallback;
};

export const isVictimCivilParty = (victimId: number, victims: any) => {
  const result = checkVictimExistanceCivilPartyById(victimId, victims);
  return Array.isArray(result) && result.includes(true);
};

export const mapVictimToJudiciaryEvaluation = (
  filtredCvilProceeding: any,
  beneficiaries: any,
  sagisEvalVictimKeys: any,
  civilDocumentsVictimKeys: any,
  isMultiple: boolean,
  isFirst: boolean,
  victimsEvaluationsToInclude: any,
  victims: any,
  desceasedVictim: VictimType[],
  judgementOnMeritsRequest: any,
  judgementOnMeritsInfo: any,
  civilJudgementVictimReasonListing: any,
  allVictims: VictimType[],
  judiciaryProcessId: any,
  claim: any,
) => {
  return get(filtredCvilProceeding, 'victims', [])
    .filter((v: any) => {
      const victimBeneficiaries = beneficiaries.filter(
        (beneficiary: any) => beneficiary?.victimId === v?.id,
      );
      return (
        v[VICTIM_EXPERTISE_FIELDS.INJURY_POST_KEY] === '2' &&
        !sagisEvalVictimKeys.includes(v.victimKey) &&
        !civilDocumentsVictimKeys.includes(v.victimKey) &&
        isJomAssociatedVictimsIncluded(
          isMultiple || isFirst,
          get(v, 'id'),
          victimsEvaluationsToInclude,
        ) &&
        !isNextOfKinsWithFormRejection(
          victimBeneficiaries,
          civilJudgementVictimReasonListing,
        )
      );
    })
    .map((victim: VictimType) => {
      const currentVictim = desceasedVictim.find(
        (el: any) => el.victimId === victim.id,
      );
      const jmv =
        get(judgementOnMeritsRequest, 'jmv', []).find(
          (item: JudgementOnMeritsVictim) =>
            item.victimId === get(victim, 'victimId', victim?.id),
        ) || {};

      return (
        checkVictimExistanceCivilPartyById(
          victim[VICTIM_EXPERTISE_FIELDS.VICTIM_ID],
          victims,
        ) &&
        checkVictimExistanceCivilPartyById(
          victim[VICTIM_EXPERTISE_FIELDS.VICTIM_ID],
          victims,
        ).includes(true) && {
          gestionMode: '',
          materialAmount: '',
          currentJudiciaryStep: CURRENT_JUDICIARY_STEP_JUDGEMENT_ON_MERITS,
          judiciaryAmount: isNextOfKinsSubstantive(
            get(currentVictim, 'beneficiaries', []),
            civilJudgementVictimReasonListing,
          )
            ? 0
            : !isNil(get(jmv, 'compensationAmount', ''))
            ? Number(get(jmv, 'compensationAmount'))
            : get(currentVictim, 'judiciaryAmount', ''),
          responsibilityRate: get(currentVictim, 'responsibilityShare', ''),
          rateRecovery: null,
          revenu: !isNil(
            getVictimRevenu(
              victim[VICTIM_EXPERTISE_FIELDS.VICTIM_ID],
              allVictims,
              judiciaryProcessId,
            ),
          )
            ? getVictimRevenu(
                victim[VICTIM_EXPERTISE_FIELDS.VICTIM_ID],
                allVictims,
                judiciaryProcessId,
              )
            : victim['annualOfIncome'],
          age: getVictimAge(
            victim['id'],
            allVictims,
            get(claim, 'lossDate', ''),
          ),
          mode: MODE.DECEASED,
          lossDate: moment(get(claim, 'lossDate', '')).toISOString(true),
          ratePretium: null,
          rateAestheticDamageWithoutConsequence: null,
          rateAestheticDamageWithConsequence: null,
          rateCareerPrejudice: null,
          rateSchoolingPrejudice: null,
          rateIPP: null,
          rateITT: null,
          victimKey: victim['victimKey'],
          medicalPharmaceuticalFees: null,
          rateMedicalPharmaceuticalFees: '',
          transactionPhase: 1,
          isJusticeFeesChecked:
            get(judgementOnMeritsInfo, 'responsibility', '') === 'Oui'
              ? true
              : false,
          isIDDChecked:
            get(judgementOnMeritsInfo, 'idd', '') === 'Oui' ? true : false,
          iddEffectiveDate: get(judgementOnMeritsInfo, 'calculationStartDate'),
          JudiciartDecisionDate: get(
            judgementOnMeritsInfo,
            'judgementDate',
            '',
          ),
          funeralFeesAmount: get(jmv, 'funeralFees', null),
          medicalFeesAmount: get(jmv, 'medicalFees', null),
        }
      );
    });
};
export const getJudiciaryAmount = (
  currentVictim: VictimType,
  jmv: any,
  civilJudgementVictimReasonListings: any,
) => {
  const isNextOfKin = isNextOfKinsSubstantive(
    get(currentVictim, 'beneficiaries', []),
    civilJudgementVictimReasonListings,
  );

  if (isNextOfKin) return 0;
  if (!isNil(jmv?.compensationAmount)) {
    return Number(jmv.compensationAmount);
  }

  return get(currentVictim, 'judiciaryAmount', '');
};
export const isEligibleCivilDocumentVictim = (
  victim: VictimType,
  beneficiaries: any,
  sagisEvalVictimKeys: any,
  isMultiple: boolean,
  isFirst: any,
  victimsEvaluationsToInclude: any,
  civilJudgementVictimReasonListings: any,
) => {
  const victimId = get(victim, 'victimId', victim?.id);

  const victimBeneficiaries = beneficiaries.filter(
    (b: any) => b?.victimId === victimId,
  );

  return (
    !sagisEvalVictimKeys.includes(victim.victimKey) &&
    isJomAssociatedVictimsIncluded(
      isMultiple || isFirst,
      (victimId as unknown) as number,
      victimsEvaluationsToInclude,
    ) &&
    !isNextOfKinsWithFormRejection(
      victimBeneficiaries,
      civilJudgementVictimReasonListings,
    )
  );
};
export const mapToCivilDocumentEvaluation = (
  victim: VictimType,
  victims: any,
  desceasedVictim: VictimType[],
  judgementOnMeritsRequest: any,
  judiciaryProcessId: any,
  claim: any,
  judgementOnMeritsInfo: any,
  civilJudgementVictimReasonListings: any,
) => {
  const victimId = get(victim, 'victimId', victim?.id);

  const currentVictim = desceasedVictim.find(
    (el: any) => el.victimId === victim.id,
  );

  const jmv =
    get(judgementOnMeritsRequest, 'jmv', []).find(
      (item: JudgementOnMeritsVictim) => item.victimId === victimId,
    ) || {};

  return {
    gestionMode: '',
    materialAmount: '',
    currentJudiciaryStep: CURRENT_JUDICIARY_STEP_JUDGEMENT_ON_MERITS,
    judiciaryAmount: getJudiciaryAmount(
      (currentVictim as unknown) as VictimType,
      jmv,
      civilJudgementVictimReasonListings,
    ),
    responsibilityRate: get(victim, 'responsibilityShare', ''),
    rateRecovery: null,
    revenu: getVictimRevenuSafe(
      victim[VICTIM_EXPERTISE_FIELDS.VICTIM_ID],
      victim.annualOfIncome,
      victims,
      judiciaryProcessId,
    ),
    age: getVictimAge(victimId, victims, get(claim, 'lossDate', '')),
    mode: MODE.DECEASED,
    lossDate: moment(get(claim, 'lossDate', '')).toISOString(true),
    funeralFeesAmount: get(jmv, 'funeralFees', null),
    medicalFeesAmount: get(jmv, 'medicalFees', null),
    ratePretium: null,
    rateAestheticDamageWithoutConsequence: null,
    rateAestheticDamageWithConsequence: null,
    rateCareerPrejudice: null,
    rateSchoolingPrejudice: null,
    rateIPP: null,
    rateITT: null,
    victimKey: victim.victimKey,
    medicalPharmaceuticalFees: null,
    rateMedicalPharmaceuticalFees: '',
    transactionPhase: 1,
    isJusticeFeesChecked:
      get(judgementOnMeritsInfo, 'responsibility') === 'Oui',
    isIDDChecked: get(judgementOnMeritsInfo, 'idd') === 'Oui',
    iddEffectiveDate: get(judgementOnMeritsInfo, 'calculationStartDate'),
    JudiciartDecisionDate: get(judgementOnMeritsInfo, 'judgementDate', ''),
  };
};

export const formatEvaluations = (
  victimIds: any,
  judgementOnMerits: any,
  judgementOnMeritsRequest: any,
  victims: any,
) => {
  return victimIds.map((id: number) => {
    let judiciaryAmount = null;
    let revenu = null;
    let itt = null;
    let expertiseJudgementAddDocumentId = null;
    let expertiseId = null;

    get(judgementOnMeritsRequest, 'civjs', []).forEach((el: any) => {
      let found = get(el, 'civjmede', []).find((el: any) => el.victimId === id);
      if (found) {
        judiciaryAmount = get(found, 'compensationAmount');
        itt = get(
          victims.find((v: any) => v.id === id),
          'itt',
        );
        expertiseJudgementAddDocumentId = get(
          found,
          'expertiseJudgementAddDocumentId',
        );
        expertiseId = EXPERTISE_MEDICAL_ID;
      } else {
        found = get(el, 'civjacce', []).find((el: any) => el.victimId === id);
        if (found) {
          judiciaryAmount = get(found, 'calculatedRevenue');
          itt = get(
            victims.find((v: any) => v.id === id),
            'itt',
          );
          expertiseJudgementAddDocumentId = get(
            found,
            'expertiseJudgementAddDocumentId',
          );
          expertiseId = EXPERTISE_ACCOUNTING_ID;
        }
      }
    });
    get(judgementOnMeritsRequest, 'civjs', []).forEach((el: any) => {
      let found = get(el, 'civjacce', []).find((el: any) => el.victimId === id);
      if (found) {
        revenu = get(found, 'evaluationAmount');
        expertiseJudgementAddDocumentId = get(
          found,
          'expertiseJudgementAddDocumentId',
        );
        expertiseId = EXPERTISE_ACCOUNTING_ID;
      }
    });
    return {
      victimId: id,
      judiciaryAmount,
      responsibilityShare: get(
        judgementOnMerits,
        'judgementOnMerits.jom.civjs[0].responsibilityShare',
        null,
      ),
      revenu,
      itt,
      expertiseJudgementAddDocumentId,
      expertiseId,
    };
  });
};

export const mapSagisEvaluationsWithoutExpertise = (
  judgementOnMeritsRequest: any,
  judgementOnMerits: any,
  judgementOnMeritsInfo: any,
  judgement: any,
  sharedResponsability: any,
  civilJudgementVictimReasonListing: any,
  judgementAddDocuments: any,
  victims: any,
  judgementAddAppeal: any,
  expertiseReport: any,
  claim: any,
  judiciaryProcessId: any,
  dahirAmount?: any,
) => {
  return formatEvaluations(
    difference(
      [
        ...uniq([
          ...uniq(getVictimIds(judgementAddDocuments)).filter(
            (id: any) =>
              ![
                ...victims
                  .filter((v: any) => v.injuryPostKey === INJURY_POST_DECEASED)
                  .map((v: any) => v.id),
              ].includes(id),
          ),
          ...uniq(
            getVictimIds(get(judgementAddAppeal, 'judgementAddDocuments', [])),
          ).filter(
            (id: any) =>
              ![
                ...victims
                  .filter((v: any) => v.injuryPostKey === INJURY_POST_DECEASED)
                  .map((v: any) => v.id),
              ].includes(id),
          ),
        ]),
      ],
      get(expertiseReport, 'victimExpertises', []).map(
        (el: any) => el[VICTIM_EXPERTISE_FIELDS.VICTIM_ID],
      ),
    ),
    judgementOnMerits,
    judgementOnMeritsRequest,
    victims,
  )
    .filter((item: any) => {
      const expertise = `${item.expertiseJudgementAddDocumentId}-${item.expertiseId}-${item.victimId}`;

      return featureService.hasFeature(FEATURES.REASONS_FOR_NON_COMPENSATION)
        ? !get(judgement[expertise], 'formRejection', false) ||
            isCivilJudgementVictimSubstantive(
              civilJudgementVictimReasonListing,
              item,
              Object.values(get(judgementOnMeritsRequest, 'civjvm', {})).filter(
                (item: any) => !isEmpty(item),
              ),
            )
        : true;
    })
    .map((el: any) => {
      const lastEvaluation = getTheLatestEvaluationObject(
        getVictimKey(
          el[VICTIM_EXPERTISE_FIELDS.VICTIM_ID] as string,
          victims || (([] as unknown) as VictimType[]),
        ),
        dahirAmount,
      );
      return (
        checkVictimExistanceCivilPartyById(
          el[VICTIM_EXPERTISE_FIELDS.VICTIM_ID],
          victims,
        ).includes(true) && {
          gestionMode: '',
          materialAmount: '',
          currentJudiciaryStep: CURRENT_JUDICIARY_STEP_JUDGEMENT_ON_MERITS,
          judiciaryAmount: isCivilJudgementVictimSubstantive(
            civilJudgementVictimReasonListing,
            el,
            Object.values(get(judgementOnMeritsRequest, 'civjvm', {})).filter(
              (item: any) => !isEmpty(item),
            ),
          )
            ? 0
            : el['judiciaryAmount'],
          responsibilityRate: sharedResponsability,
          revenu: !isNil(
            getVictimRevenu(
              el[VICTIM_EXPERTISE_FIELDS.VICTIM_ID],
              victims,
              judiciaryProcessId,
            ),
          )
            ? getVictimRevenu(
                el[VICTIM_EXPERTISE_FIELDS.VICTIM_ID],
                victims,
                judiciaryProcessId,
              )
            : el['revenu'],
          age: getVictimAge(
            el[VICTIM_EXPERTISE_FIELDS.VICTIM_ID],
            victims,
            get(claim, 'lossDate', ''),
          ),
          mode: MODE.BLESSE,
          lossDate: moment(get(claim, 'lossDate', '')).toISOString(true),
          rateITT: featureService.hasFeature(
            FEATURES.RESOLVE_EXPERTISE_REPORT_VALUES,
          )
            ? el[VICTIM_EXPERTISE_FIELDS.ITT]
              ? el[VICTIM_EXPERTISE_FIELDS.ITT]
              : String(get(lastEvaluation, 'rateITT'))
            : el[VICTIM_EXPERTISE_FIELDS.ITT],
          victimKey: getVictimKey(
            el[VICTIM_EXPERTISE_FIELDS.VICTIM_ID] as string,
            victims,
          ),
          rateMedicalPharmaceuticalFees: '',
          transactionPhase: 1,
          isJusticeFeesChecked:
            get(judgementOnMeritsInfo, 'responsibility', '') === 'Oui'
              ? true
              : false,
          isIDDChecked:
            get(judgementOnMeritsInfo, 'idd', '') === 'Oui' ? true : false,
          iddEffectiveDate: get(judgementOnMeritsInfo, 'calculationStartDate'),
          JudiciartDecisionDate: get(
            judgementOnMeritsInfo,
            'judgementDate',
            '',
          ),
          isRecentExpertiseVictim: el.isRecentExpertiseVictim,
        }
      );
    });
};
const resolveLawyerCompensationAmount = (
  el: any,
  desceasedVictims: any,
  judgementOnMeritsRequest: any,
  civilJudgementVictimReasonListings: any,
) => {
  let deceasedVictim = null;
  let victim = null;
  const civjAppeal = getCivilJudgementByVictimId(
    get(judgementOnMeritsRequest, 'civjs', []),
    el,
  );
  if (el[VICTIM_EXPERTISE_FIELDS.INJURY_POST_KEY] === '2') {
    const jmv =
      get(judgementOnMeritsRequest, 'jmv', []).find(
        (item: JudgementOnMeritsVictim) => item.victimId === el.victimId,
      ) || {};
    deceasedVictim =
      desceasedVictims.find((item: any) => item.victimId === el.victimId) || {};
    return isNextOfKinsSubstantive(
      get(deceasedVictim, 'beneficiaries', []),
      civilJudgementVictimReasonListings,
    )
      ? 0
      : Number(
          get(
            jmv,
            'compensationAmount',
            get(deceasedVictim, 'judiciaryAmount', null),
          ),
        );
  }
  if (el.expertiseId === 1 && !el.isFirstInstance) {
    const victim = civjAppeal.find(cj => !isEmpty(cj.civjmede));

    return get(victim, 'civjmede[0].compensationAmount', '');
  } else if (el.expertiseId === 1 && el.isFirstInstance) {
    const victim = civjAppeal.find(cj => !isEmpty(cj.civjmede));

    return get(victim, 'civjmede[0].compensationAmount', '');
  }
  !el.isFirstInstance
    ? (victim = civjAppeal.find(cj => !isEmpty(cj.civjacce)))
    : (victim = civjAppeal.find(cj => !isEmpty(cj.civjacce)));

  return get(victim, 'civjacce[0].calculatedRevenue', '');
};

const resolveRevenu = (el: any, judgementOnMeritsRequest: any) => {
  const civjAppeal = getCivilJudgementByVictimId(
    get(judgementOnMeritsRequest, 'civjs', []),
    el,
  );

  if (el[VICTIM_EXPERTISE_FIELDS.INJURY_POST_KEY] === '2')
    return el.expertiseId === 2
      ? el[VICTIM_EXPERTISE_FIELDS.FIXED_ANNUAL_INCOME]
      : el[VICTIM_EXPERTISE_FIELDS.VICTIM_REVENUE];

  if (!el.isFirstInstance) {
    const victim = civjAppeal.find(cj => !isEmpty(cj.civjacce));
    return !isNil(victim)
      ? get(victim, 'civjacce[0].evaluationAmount', el?.fixedAnnualIncome)
      : !isNil(el?.fixedAnnualIncome)
      ? el?.fixedAnnualIncome
      : el[VICTIM_EXPERTISE_FIELDS.VICTIM_REVENUE];
  }
  const cjAppeal = civjAppeal.find(cj => !isEmpty(cj.civjacce));
  return !isNil(cjAppeal)
    ? get(cjAppeal, 'civjacce[0].evaluationAmount', el?.fixedAnnualIncome)
    : !isNil(el?.fixedAnnualIncome)
    ? el?.fixedAnnualIncome
    : el[VICTIM_EXPERTISE_FIELDS.VICTIM_REVENUE];
};
export const mapSagisEvaluations = (
  allVictimExpertises: any,
  desceasedVictim: VictimType[],
  isMultiple: any,
  isFirst: any,
  victimsEvaluationsToInclude: any,
  judgementOnMeritsRequest: any,
  judgement: any,
  judgementOnMeritsInfo: any,
  civilJudgementOnMeritsForm: any,
  responsibilityRate: any,
  civilJudgementVictimReasonListing: any,
  victims: VictimType[],
  judiciaryProcessId: any,
  claim: any,
  expertiseReport?: any,
  dahirAmount?: any,
) => {
  return allVictimExpertises
    .filter((el: any) => {
      const victim: VictimType | {} =
        desceasedVictim.find(
          (item: VictimType) => item?.victimId === el?.victimId,
        ) || {};
      return (
        isJomAssociatedVictimsIncluded(
          isMultiple || isFirst,
          el[VICTIM_EXPERTISE_FIELDS.VICTIM_ID],
          victimsEvaluationsToInclude,
        ) &&
        !isNextOfKinsWithFormRejection(
          get(victim, 'beneficiaries', []),
          civilJudgementVictimReasonListing,
        ) &&
        reasonWithoutCompensationConditions(
          el,
          judgement,
          Object.values(get(judgementOnMeritsRequest, 'civjvm', {})).filter(
            (item: any) => !isEmpty(item),
          ),
          (victim as unknown) as VictimType,
          civilJudgementVictimReasonListing,
        )
      );
    })
    .map((el: any) => {
      const currentVictim = desceasedVictim.find(
        (vic: any) => vic.victimId === el.victimId,
      );
      const lastMedicalExpertise =
        get(expertiseReport, 'victimExpertises', []).find(
          (item: any) =>
            String(item?.victimId) === String(el?.victimId) &&
            !item.isOld &&
            item.status !== GLOBAL_STATUS.PENDING &&
            item.status !== GLOBAL_STATUS.REJECTED &&
            !item.isRejectedJudgement &&
            String(item.expertiseId) === MEDICAL_EXPERTISE_ID,
        ) || {};
      const lastEvaluation = getTheLatestEvaluationObject(
        getVictimKey(
          el[VICTIM_EXPERTISE_FIELDS.VICTIM_ID] as string,
          victims || (([] as unknown) as VictimType[]),
        ),
        dahirAmount,
      );
      const objectToReturn = featureService.hasFeature(
        FEATURES.RESOLVE_EXPERTISE_REPORT_VALUES,
      )
        ? {
            gestionMode: '',
            materialAmount: '',
            currentJudiciaryStep: CURRENT_JUDICIARY_STEP_JUDGEMENT_ON_MERITS,
            judiciaryAmount: isCivilJudgementVictimSubstantive(
              civilJudgementVictimReasonListing,
              el,
              Object.values(get(judgementOnMeritsRequest, 'civjvm', {})).filter(
                (item: any) => !isEmpty(item),
              ),
            )
              ? 0
              : resolveLawyerCompensationAmount(
                  el,
                  desceasedVictim,
                  judgementOnMeritsRequest,
                  civilJudgementVictimReasonListing,
                ),
            responsibilityRate: currentVictim
              ? get(currentVictim, 'responsibilityShare', '')
              : getResponsibilityShareByType(
                  get(judgementOnMeritsRequest, 'civjs', []),
                  allVictimExpertises,
                  desceasedVictim,
                  el,
                  civilJudgementOnMeritsForm,
                  responsibilityRate,
                ),
            rateRecovery: resolveExpertiseValue(
              el,
              el[VICTIM_EXPERTISE_FIELDS.THIRD_PARTY_APPEAL],
              lastMedicalExpertise,
              lastMedicalExpertise[VICTIM_EXPERTISE_FIELDS.THIRD_PARTY_APPEAL],
              get(lastEvaluation, 'rateRecovery'),
            ),
            revenu: !isNil(
              getVictimRevenu(
                el[VICTIM_EXPERTISE_FIELDS.VICTIM_ID],
                victims,
                judiciaryProcessId,
              ),
            )
              ? getVictimRevenu(
                  el[VICTIM_EXPERTISE_FIELDS.VICTIM_ID],
                  victims,
                  judiciaryProcessId,
                )
              : resolveRevenu(el, judgementOnMeritsRequest),
            age: getVictimAge(
              el[VICTIM_EXPERTISE_FIELDS.VICTIM_ID],
              victims,
              get(claim, 'lossDate', ''),
            ),
            lossDate: moment(get(claim, 'lossDate', '')).toISOString(true),
            mode:
              el[VICTIM_EXPERTISE_FIELDS.INJURY_POST_KEY] === '2'
                ? MODE.DECEASED
                : MODE.BLESSE,
            ratePretium: resolveExpertiseValue(
              el,
              el[VICTIM_EXPERTISE_FIELDS.PRETIUM_DOLORIS],
              lastMedicalExpertise,
              lastMedicalExpertise[VICTIM_EXPERTISE_FIELDS.PRETIUM_DOLORIS],
              get(lastEvaluation, 'ratePretium'),
            ),
            rateAestheticDamageWithoutConsequence: resolveExpertiseValue(
              el,
              el[VICTIM_EXPERTISE_FIELDS.AESTHETIC_DAMAGE_WITHOUT_CONSEQUENCE],
              lastMedicalExpertise,
              lastMedicalExpertise[
                VICTIM_EXPERTISE_FIELDS.AESTHETIC_DAMAGE_WITHOUT_CONSEQUENCE
              ],
              get(lastEvaluation, 'rateAestheticDamageWithoutConsequence'),
            ),
            rateAestheticDamageWithConsequence: resolveExpertiseValue(
              el,
              el[VICTIM_EXPERTISE_FIELDS.AESTHETIC_DAMAGE_WITH_CONSEQUENCE],
              lastMedicalExpertise,
              lastMedicalExpertise[
                VICTIM_EXPERTISE_FIELDS.AESTHETIC_DAMAGE_WITH_CONSEQUENCE
              ],
              get(lastEvaluation, 'rateAestheticDamageWithConsequence'),
            ),
            rateCareerPrejudice: resolveExpertiseValue(
              el,
              el[VICTIM_EXPERTISE_FIELDS.CAREER_PREJUDICE],
              lastMedicalExpertise,
              lastMedicalExpertise[VICTIM_EXPERTISE_FIELDS.CAREER_PREJUDICE],
              get(lastEvaluation, 'rateCareerPrejudice'),
            ),
            rateSchoolingPrejudice: resolveExpertiseValue(
              el,
              el[VICTIM_EXPERTISE_FIELDS.SCHOOLING_PREJUDICE],
              lastMedicalExpertise,
              lastMedicalExpertise[VICTIM_EXPERTISE_FIELDS.SCHOOLING_PREJUDICE],
              get(lastEvaluation, 'rateSchoolingPrejudice'),
            ),
            rateIPP: resolveExpertiseValue(
              el,
              el[VICTIM_EXPERTISE_FIELDS.IPP],
              lastMedicalExpertise,
              lastMedicalExpertise[VICTIM_EXPERTISE_FIELDS.IPP],
              get(lastEvaluation, 'rateIPP'),
            ),
            rateITT: getRateItt(el, lastMedicalExpertise, lastEvaluation),
            victimKey: getVictimKey(
              el[VICTIM_EXPERTISE_FIELDS.VICTIM_ID] as string,
              victims,
            ),
            medicalPharmaceuticalFees:
              el[VICTIM_EXPERTISE_FIELDS.MEDICAL_PHARMACEUTICAL_FEES],
            // rateMedicalPharmaceuticalFees: '',
            transactionPhase: 1,
            isJusticeFeesChecked:
              get(judgementOnMeritsInfo, 'responsibility', '') === 'Oui'
                ? true
                : false,
            isIDDChecked:
              get(judgementOnMeritsInfo, 'idd', '') === 'Oui' ? true : false,
            iddEffectiveDate: get(
              judgementOnMeritsInfo,
              'calculationStartDate',
            ),
            JudiciartDecisionDate: get(
              judgementOnMeritsInfo,
              'judgementDate',
              '',
            ),
            expType: Number(el.expertiseId),
            isRecentExpertiseVictim: el.isRecentExpertiseVictim,
            funeralFeesAmount:
              el[VICTIM_EXPERTISE_FIELDS.INJURY_POST_KEY] === '2'
                ? get(
                    resolveDeceasedVictimFees(
                      el,
                      get(judgementOnMeritsRequest, 'jmv', []),
                    ),
                    'funeralFees',
                    null,
                  )
                : undefined,
            medicalFeesAmount:
              el[VICTIM_EXPERTISE_FIELDS.INJURY_POST_KEY] === '2'
                ? get(
                    resolveDeceasedVictimFees(
                      el,
                      get(judgementOnMeritsRequest, 'jmv', []),
                    ),
                    'medicalFees',
                    null,
                  )
                : undefined,
          }
        : {
            gestionMode: '',
            materialAmount: '',
            currentJudiciaryStep: CURRENT_JUDICIARY_STEP_JUDGEMENT_ON_MERITS,
            judiciaryAmount: isCivilJudgementVictimSubstantive(
              civilJudgementVictimReasonListing,
              el,
              Object.values(get(judgementOnMeritsRequest, 'civjvm', {})).filter(
                (item: any) => !isEmpty(item),
              ),
            )
              ? 0
              : resolveLawyerCompensationAmount(
                  el,
                  desceasedVictim,
                  judgementOnMeritsRequest,
                  civilJudgementVictimReasonListing,
                ),
            responsibilityRate: currentVictim
              ? get(currentVictim, 'responsibilityShare', '')
              : getResponsibilityShareByType(
                  get(judgementOnMeritsRequest, 'civjs', []),
                  allVictimExpertises,
                  desceasedVictim,
                  el,
                  civilJudgementOnMeritsForm,
                  responsibilityRate,
                ),
            rateRecovery: el[VICTIM_EXPERTISE_FIELDS.THIRD_PARTY_APPEAL],
            revenu: !isNil(
              getVictimRevenu(
                el[VICTIM_EXPERTISE_FIELDS.VICTIM_ID],
                victims,
                judiciaryProcessId,
              ),
            )
              ? getVictimRevenu(
                  el[VICTIM_EXPERTISE_FIELDS.VICTIM_ID],
                  victims,
                  judiciaryProcessId,
                )
              : resolveRevenu(el, judgementOnMeritsRequest),
            age: getVictimAge(
              el[VICTIM_EXPERTISE_FIELDS.VICTIM_ID],
              victims,
              get(claim, 'lossDate', ''),
            ),
            lossDate: moment(get(claim, 'lossDate', '')).toISOString(true),
            mode:
              el[VICTIM_EXPERTISE_FIELDS.INJURY_POST_KEY] === '2'
                ? MODE.DECEASED
                : MODE.BLESSE,
            ratePretium: el[VICTIM_EXPERTISE_FIELDS.PRETIUM_DOLORIS],
            rateAestheticDamageWithoutConsequence:
              el[VICTIM_EXPERTISE_FIELDS.AESTHETIC_DAMAGE_WITHOUT_CONSEQUENCE],
            rateAestheticDamageWithConsequence:
              el[VICTIM_EXPERTISE_FIELDS.AESTHETIC_DAMAGE_WITH_CONSEQUENCE],
            rateCareerPrejudice: el[VICTIM_EXPERTISE_FIELDS.CAREER_PREJUDICE],
            rateSchoolingPrejudice:
              el[VICTIM_EXPERTISE_FIELDS.SCHOOLING_PREJUDICE],
            rateIPP: el[VICTIM_EXPERTISE_FIELDS.IPP],
            rateITT: el[VICTIM_EXPERTISE_FIELDS.ITT]
              ? el[VICTIM_EXPERTISE_FIELDS.ITT]
              : el.previousItt,
            victimKey: getVictimKey(
              el[VICTIM_EXPERTISE_FIELDS.VICTIM_ID] as string,
              victims,
            ),
            medicalPharmaceuticalFees:
              el[VICTIM_EXPERTISE_FIELDS.MEDICAL_PHARMACEUTICAL_FEES],
            rateMedicalPharmaceuticalFees: '',
            transactionPhase: 1,
            isJusticeFeesChecked:
              get(judgementOnMeritsInfo, 'responsibility', '') === 'Oui'
                ? true
                : false,
            isIDDChecked:
              get(judgementOnMeritsInfo, 'idd', '') === 'Oui' ? true : false,
            iddEffectiveDate: get(
              judgementOnMeritsInfo,
              'calculationStartDate',
            ),
            JudiciartDecisionDate: get(
              judgementOnMeritsInfo,
              'judgementDate',
              '',
            ),
            expType: Number(el.expertiseId),
            isRecentExpertiseVictim: el.isRecentExpertiseVictim,
            funeralFeesAmount:
              el[VICTIM_EXPERTISE_FIELDS.INJURY_POST_KEY] === '2'
                ? get(
                    resolveDeceasedVictimFees(
                      el,
                      get(judgementOnMeritsRequest, 'jmv', []),
                    ),
                    'funeralFees',
                    null,
                  )
                : undefined,
            medicalFeesAmount:
              el[VICTIM_EXPERTISE_FIELDS.INJURY_POST_KEY] === '2'
                ? get(
                    resolveDeceasedVictimFees(
                      el,
                      get(judgementOnMeritsRequest, 'jmv', []),
                    ),
                    'medicalFees',
                    null,
                  )
                : undefined,
          };
      return (
        checkVictimExistanceCivilPartyById(
          el[VICTIM_EXPERTISE_FIELDS.VICTIM_ID],
          victims,
        ) &&
        checkVictimExistanceCivilPartyById(
          el[VICTIM_EXPERTISE_FIELDS.VICTIM_ID],
          victims,
        ).includes(true) &&
        objectToReturn
      );
    });
};

export const mapCivilDocumentsEvaluations = (
  desceasedVictim: VictimType[],
  victims: VictimType[],
  beneficiaries: any,
  sagisEvalVictimKeys: any,
  isMultiple: any,
  isFirst: any,
  victimsEvaluationsToInclude: any,
  judgementOnMeritsRequest: any,
  judgementOnMeritsInfo: any,
  civilJudgementVictimReasonListing: any,
  allVictims: VictimType[],
  judiciaryProcessId: any,
  claim: any,
) => {
  return checkVictimExistanceCivilParty(desceasedVictim, victims) &&
    checkVictimExistanceCivilParty(desceasedVictim, victims).includes(true)
    ? desceasedVictim
        .filter((v: any) => {
          const victimBeneficiaries = beneficiaries.filter(
            (beneficiary: any) =>
              beneficiary?.victimId === get(v, 'victimId', v?.id),
          );
          return (
            !sagisEvalVictimKeys.includes(v.victimKey) &&
            isJomAssociatedVictimsIncluded(
              isMultiple || isFirst,
              get(v, 'victimId', v?.id),
              victimsEvaluationsToInclude,
            ) &&
            !isNextOfKinsWithFormRejection(
              victimBeneficiaries,
              civilJudgementVictimReasonListing,
            )
          );
        })
        .map((victim: VictimType) => {
          const currentVictim = desceasedVictim.find(
            (el: any) => el.victimId === victim.id,
          );
          const jmv =
            get(judgementOnMeritsRequest, 'jmv', []).find(
              (item: JudgementOnMeritsVictim) =>
                item.victimId === get(victim, 'victimId', victim?.id),
            ) || {};
          return {
            judiciaryAmount: isNextOfKinsSubstantive(
              get(currentVictim, 'beneficiaries', []),
              civilJudgementVictimReasonListing,
            )
              ? 0
              : !isNil(get(jmv, 'compensationAmount', null))
              ? Number(get(jmv, 'compensationAmount'))
              : get(currentVictim, 'judiciaryAmount', ''),
            gestionMode: '',
            materialAmount: '',
            currentJudiciaryStep: CURRENT_JUDICIARY_STEP_JUDGEMENT_ON_MERITS,

            responsibilityRate: get(victim, 'responsibilityShare', ''),
            rateRecovery: null,
            revenu: !isNil(
              getVictimRevenu(victim.id, allVictims, judiciaryProcessId),
            )
              ? getVictimRevenu(victim.id, allVictims, judiciaryProcessId)
              : victim['annualOfIncome'],
            age: getVictimAge(
              get(victim, 'victimId', victim?.id),
              allVictims,
              get(claim, 'lossDate', ''),
            ),
            lossDate: moment(get(claim, 'lossDate', '')).toISOString(true),
            mode: MODE.DECEASED,
            funeralFeesAmount: get(jmv, 'funeralFees', null),
            medicalFeesAmount: get(jmv, 'medicalFees', null),
            ratePretium: null,
            rateAestheticDamageWithoutConsequence: null,
            rateAestheticDamageWithConsequence: null,
            rateCareerPrejudice: null,
            rateSchoolingPrejudice: null,
            rateIPP: null,
            rateITT: null,
            victimKey: victim['victimKey'],
            medicalPharmaceuticalFees: null,
            rateMedicalPharmaceuticalFees: '',
            transactionPhase: 1,
            isJusticeFeesChecked:
              get(judgementOnMeritsInfo, 'responsibility', '') === 'Oui'
                ? true
                : false,
            isIDDChecked:
              get(judgementOnMeritsInfo, 'idd', '') === 'Oui' ? true : false,
            iddEffectiveDate: get(
              judgementOnMeritsInfo,
              'calculationStartDate',
            ),
            JudiciartDecisionDate: get(
              judgementOnMeritsInfo,
              'judgementDate',
              '',
            ),
          };
        })
    : [];
};

export const mapReasonWithoutCompensationEvaluations = (
  judgementOnMeritsRequest: any,
  victims: VictimType[],
  sharedResponsability: any,
  judgementOnMeritsInfo: any,
  civilJudgementVictimReasonListing: any,
  allVictims: any,
  claim: any,
  judiciaryProcessId: any,
  expertiseReport: any,
  dahirAmount: any,
) => {
  return getSubstantiveWithoutCompensation(
    civilJudgementVictimReasonListing,
    Object.values(get(judgementOnMeritsRequest, 'civjvm', {})).filter(
      (item: any) => !isEmpty(item),
    ),
  ).map((item: any) => {
    const victim =
      victims.find(
        (victim: VictimType) => victim?.id === get(item, 'victimId'),
      ) || {};
    const lastMedicalExpertise =
      get(expertiseReport, 'victimExpertises', []).find(
        (el: any) =>
          String(el?.victimId) === String(item?.victimId) &&
          !el.isOld &&
          el.status !== GLOBAL_STATUS.PENDING &&
          el.status !== GLOBAL_STATUS.REJECTED &&
          !el.isRejectedJudgement &&
          String(el.expertiseId) === MEDICAL_EXPERTISE_ID,
      ) || {};
    const lastEvaluation = getTheLatestEvaluationObject(
      getVictimKey(
        item[VICTIM_EXPERTISE_FIELDS.VICTIM_ID] as string,
        victims || (([] as unknown) as VictimType[]),
      ),
      dahirAmount,
    );
    return (
      checkVictimExistanceCivilPartyById(
        item[VICTIM_EXPERTISE_FIELDS.VICTIM_ID],
        victims,
      ).includes(true) && {
        gestionMode: '',
        materialAmount: '',
        currentJudiciaryStep: CURRENT_JUDICIARY_STEP_JUDGEMENT_ON_MERITS,
        judiciaryAmount: 0,
        responsibilityRate: sharedResponsability,
        age: getVictimAge(
          item[VICTIM_EXPERTISE_FIELDS.VICTIM_ID],
          allVictims,
          get(claim, 'lossDate', ''),
        ),
        revenu: getVictimRevenu(
          get(item, 'victimId'),
          allVictims,
          judiciaryProcessId,
        ),
        lossDate: moment(get(claim, 'lossDate', '')).toISOString(true),
        mode: MODE.BLESSE,
        rateITT: featureService.hasFeature(
          FEATURES.RESOLVE_EXPERTISE_REPORT_VALUES,
        )
          ? resolveExpertiseValue(
              item,
              item[VICTIM_EXPERTISE_FIELDS.ITT],
              lastMedicalExpertise,
              lastMedicalExpertise[VICTIM_EXPERTISE_FIELDS.ITT],
              get(lastEvaluation, 'rateITT'),
            )
          : get(victim, [VICTIM_EXPERTISE_FIELDS.ITT]),
        victimKey: getVictimKey(
          item[VICTIM_EXPERTISE_FIELDS.VICTIM_ID] as string,
          allVictims,
        ),
        rateMedicalPharmaceuticalFees: '',
        transactionPhase: 1,
        isJusticeFeesChecked:
          get(judgementOnMeritsInfo, 'responsibility', '') === 'Oui'
            ? true
            : false,
        isIDDChecked:
          get(judgementOnMeritsInfo, 'idd', '') === 'Oui' ? true : false,
        iddEffectiveDate: get(judgementOnMeritsInfo, 'calculationStartDate'),
        JudiciartDecisionDate: get(judgementOnMeritsInfo, 'judgementDate', ''),
      }
    );
  });
};
export const getFeeNoteAssignment = (
  claimFeeNotes: any,
  judgementOnMerits: any,
) => {
  return claimFeeNotes
    .sort(
      (a: any, b: any) =>
        new Date(get(a, 'createdAt')).getTime() -
        new Date(get(b, 'createdAt')).getTime(),
    )
    .find((el: any) =>
      el.judgementId
        ? el.judgementId === get(judgementOnMerits, 'judgementOnMerits.id') &&
          el.codeIntervenant === currentIntervenantStorage.get()?.sagis_code
        : el.step === PROCEDURE_STEPS.ARRET_ON_MERITS &&
          new Date(el.createdAt).getTime() >=
            new Date(
              get(judgementOnMerits, 'judgementOnMerits.createdAt'),
            ).getTime(),
    );
};
